import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { cn } from '../../cn';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;

// Positioner-only props are derived from Base UI's own type — not enumerated
// by hand — so any future addition to the anchor-positioning surface flows
// in automatically. `Omit<Positioner.Props, keyof Popup.Props>` strips the
// named keys that both share via BaseUIComponentProps (className / children /
// render / ref / style / id / role / tabIndex / aria-*-named / on*-handlers).
// The `data-*` index signature passes through Omit, so callers can still set
// `data-*` attributes via positionerProps if they want them on the Positioner.
type PopoverPositionerOnlyProps = Omit<
  PopoverPrimitive.Positioner.Props,
  keyof PopoverPrimitive.Popup.Props
>;

type PopoverContentProps = PopoverPrimitive.Popup.Props & {
  /** Forwarded to Positioner. Lets callers tune side/align/collision/etc. */
  positionerProps?: PopoverPositionerOnlyProps;
  /** Extra classes for Positioner — primarily for z-index override when nested in another portal/modal. */
  positionerClassName?: string;
};

export function PopoverContent({
  className,
  positionerProps,
  positionerClassName,
  // Lift portal-owner attribution to the Positioner so click-outside
  // detectors using target.closest('[data-portal-owner]') match the entire
  // owned subtree, not just Popup. Caller-supplied data-portal-owner
  // overrides our default of "popover".
  'data-portal-owner': dataPortalOwner = 'popover',
  ...props
}: PopoverContentProps & { 'data-portal-owner'?: string }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        sideOffset={8}
        {...positionerProps}
        className={cn('z-50', positionerClassName)}
        data-portal-owner={dataPortalOwner}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            'w-auto rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md outline-none',
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}
